import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useLocationContext } from "../../contexts/locationContext";
import { handleLogin } from "../../shared/controllers/authController";
import { toStringValue, unwrapApiRecord } from "../../shared/utils/apiResponse";
import { getCountryNameFromIso3, toIso3 } from "../../utils/locationStorage";
import { Profile } from "../../utils/walletSnapshot";
import { useUserContext } from "../contexts/userContext";

import authbg from "./assets/authbg.svg";
import load from "./assets/load.gif";
import logo from "./assets/logo.svg";



interface FormErrors {
    email?: string;
    password?: string;
}

const pickFirstNonEmptyRecord = (
    ...values: unknown[]
): Record<string, unknown> | null => {
    for (const value of values) {
        const record = unwrapApiRecord<Record<string, unknown>>(value);
        if (Object.keys(record).length > 0) {
            return record;
        }
    }
    return null;
};

const Signin: React.FC = () => {
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [errors, setErrors] = useState<FormErrors>({});
    const { saveUserProfile, refreshUserProfile } = useUserContext();
    const { updateLocation } = useLocationContext();
    const requestedRedirect = searchParams.get("redirect") || "";
    const redirectPath = requestedRedirect.startsWith("/") && !requestedRedirect.startsWith("//")
        ? requestedRedirect
        : "/dashboard";

    useEffect(() => {
        const storedItem = localStorage.getItem("saca_token");
        if (storedItem) {
            navigate(redirectPath, { replace: true });
        }
    }, [navigate, redirectPath]);

    // For handleLogin
    const onSuccess = async (response: unknown) => {
        setLoading(false);

        const payload = unwrapApiRecord<Record<string, unknown>>(response);
        const dataPayload = unwrapApiRecord<Record<string, unknown>>(payload.data);
        const nestedData = unwrapApiRecord<Record<string, unknown>>(dataPayload.data);
        const user = pickFirstNonEmptyRecord(
            dataPayload.user,
            payload.user,
            nestedData.user,
            payload.id || payload.email ? payload : null
        );

        if (user && Object.keys(user).length > 0) {
            saveUserProfile?.(user as unknown as Profile);
            const countryData = unwrapApiRecord<Record<string, unknown>>(user.country);

            const countryCode = toIso3(
                toStringValue(user.country_code) ||
                toStringValue(countryData.iso3) ||
                toStringValue(countryData.iso_3) ||
                toStringValue(countryData.code) ||
                toStringValue(countryData.iso2) ||
                toStringValue(countryData.iso_2) ||
                toStringValue(user.country) ||
                ""
            );

            if (countryCode) {
                const countryName =
                    toStringValue(countryData.name) ||
                    toStringValue(user.country) ||
                    getCountryNameFromIso3(countryCode) ||
                    "";
                await updateLocation({ countryCode, countryName });
            }
        } else {
            // Attempt to bootstrap location when user data is missing in login response
            try {
                await refreshUserProfile?.();
            } catch (error) {
                console.warn("Unable to refresh profile post-login:", error);
            }
        }

        await refreshUserProfile?.();
        navigate(redirectPath);
    };

    const onError = () => {
        setLoading(false);
    };

    const validateFields = (): boolean => {
        const newErrors: FormErrors = {};
        if (!email) newErrors.email = "Email is required";
        if (!password) newErrors.password = "Password is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateFields()) {
            setLoading(true);
            const userData = {
                email,
                password,
            };
            handleLogin(userData, "user", onSuccess, onError);
        }
    };

    return (
        <>
            <div
                style={{
                    backgroundImage: `url(${authbg})`,
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                }}
                className="w-full md:w-[120%] h-full bg-[#1212128d] z-[999] fixed top-0 left-0 md:-left-[20%] p-6 flex justify-center items-center"
            >
                <div className="md:ml-[20%] h-[550px] bg-[#FFFFFF] p-6 rounded-[15px] w-full md:w-[550px]">
                    <div className="w-full h-full bg-[#fff] overflow-y-auto p-4 md:p-4 flex flex-col items-center">
                        <img src={logo} className=" w-[20%]" alt="" />
                        <p className=" mt-6 text-center font-Manrope font-bold text-[#272D37] text-3xl">
                            Welcome back!
                        </p>

                        <label
                            htmlFor=""
                            className=" flex flex-col w-full font-Manrope font-semibold text-sm text-[#272D37] mt-6"
                        >
                            Email
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className=" mt-2 border border-[#DAE0E6] px-4 py-3 rounded-md font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                                placeholder="Enter your Email"
                            />
                            {errors.email && (
                                <span className="text-red-500 text-xs font-Manrope mt-2">
                                    {errors.email}
                                </span>
                            )}
                        </label>
                        <label
                            htmlFor=""
                            className=" flex flex-col  w-full font-Manrope font-semibold text-sm text-[#272D37] mt-4"
                        >
                            Password
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className=" mt-2 border border-[#DAE0E6] px-4 py-3 rounded-md font-Manrope font-normal text-[15px] placeholder:text-[#919BA7]"
                                placeholder="Enter your Password"
                            />
                            {errors.password && (
                                <span className="text-red-500 text-xs font-Manrope mt-2">
                                    {errors.password}
                                </span>
                            )}
                        </label>

                        <div className=" flex items-center justify-between w-full">
                            <p></p>
                            <Link
                                to="/reset-password"
                                className=" text-[#317333] mt-3 font-Manrope text-[15px] font-bold"
                            >
                                Forgot password?
                            </Link>
                        </div>
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className=" bg-[#317333] rounded-md w-full mt-6 py-3 font-Manrope text-[#fff] font-semibold text-base flex justify-center  items-center"
                        >
                            {loading ? (
                                <img src={load} className=" w-5" alt="Loading" />
                            ) : (
                                "Sign in"
                            )}
                        </button>
                        <p className=" mt-2 font-normal text-sm font-Manrope text-[#5F6D7E]">
                            Don’t have an account?{" "}
                            <Link
                                to={`/sign-up?redirect=${encodeURIComponent(redirectPath)}`}
                                className=" font-Manrope text-[#317333] font-bold text-[15px]"
                            >
                                Sign Up
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Signin;
